import { useState } from 'react';

/**
 * Composition comparison — who owns each layer of the stack.
 *
 * Emphasis form: accent for the integrated stack, de-emphasis gray for the
 * assembled one. The gray is deliberately low-chroma (it is context, not a
 * second category); separation from the accent is what matters —
 * CVD ΔE 16.8, normal-vision ΔE 16.9, both above floor against #0f172a.
 *
 * One CSS grid carries both columns so the three layers align row to row —
 * the comparison only reads if "Service" sits opposite "Service".
 * The argument is carried by the seams: an assembled stack has vendor
 * boundaries between the blocks, an owned one has hairlines.
 */

interface Layer {
  name: string;
  ours: string;
  theirs: string;
  gain: string;
}

const layers: Layer[] = [
  {
    name: 'Service',
    ours: 'Built, sold and supported by DialogMe',
    theirs: 'Built in-house, or licensed from an application vendor',
    gain: 'We own the customer relationship and the roadmap. Nobody sits between us and the buyer.',
  },
  {
    name: 'AI Framework',
    ours: 'The Fnk Harness and framework — our own',
    theirs: 'An open-source framework, on someone else’s release cycle',
    gain: 'No waiting on an upstream fix, and no breaking change we did not choose — it is our own stack and our own roadmap.',
  },
  {
    name: 'Inference',
    ours: 'The Fnk engine, on hardware DialogMe operates',
    theirs: 'Rented from a model vendor, billed per token',
    gain: 'Cost is fixed infrastructure, not a bill that grows every time we succeed.',
  },
];

const ACCENT = '#6366f1';
const CONTEXT = '#64748b';

export function VerticalIntegrationChart() {
  const [active, setActive] = useState<string | null>(null);

  return (
    <figure className="m-0">
      {/* column headers */}
      <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-3">
        <div className="text-center">
          <span className="text-xs sm:text-sm font-semibold text-slate-400">Others</span>
          <p className="text-[10px] sm:text-[11px] text-slate-600 mt-0.5">three vendors, three bills</p>
        </div>
        <div className="text-center">
          <span className="text-xs sm:text-sm font-semibold text-white">DialogMe on Fnk</span>
          <p className="text-[10px] sm:text-[11px] text-indigo-300/70 mt-0.5">one founder, no vendor seam</p>
        </div>
      </div>

      {/* one grid: rows align because both cells share a row track */}
      <div className="grid grid-cols-2 gap-x-3 sm:gap-x-6">
        {layers.map((layer, i) => {
          const dim = active !== null && active !== layer.name;
          const first = i === 0;
          const last = i === layers.length - 1;
          return (
            <div key={layer.name} className="contents">
              {/* assembled — separate block, seam below */}
              <div className={i > 0 ? 'pt-5 sm:pt-6' : undefined}>
                <div
                  className="h-full rounded-md px-2 py-3 sm:px-4 sm:py-4 text-center transition-opacity"
                  style={{
                    background: 'rgba(100,116,139,0.10)',
                    border: `1px solid ${CONTEXT}55`,
                    opacity: dim ? 0.45 : 1,
                  }}
                >
                  <div className="text-[11px] sm:text-sm font-medium text-slate-400">{layer.name}</div>
                  <div className="text-[9px] sm:text-[11px] text-slate-600 leading-snug mt-1">
                    {layer.theirs}
                  </div>
                </div>
              </div>

              {/* integrated — one continuous block, hairline dividers */}
              <div
                className="px-2 py-3 sm:px-4 sm:py-4 text-center cursor-default transition-colors"
                style={{
                  background: active === layer.name ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
                  borderLeft: `1.5px solid ${ACCENT}`,
                  borderRight: `1.5px solid ${ACCENT}`,
                  borderTop: first ? `1.5px solid ${ACCENT}` : `1px solid ${ACCENT}33`,
                  borderBottom: last ? `1.5px solid ${ACCENT}` : undefined,
                  borderTopLeftRadius: first ? 6 : undefined,
                  borderTopRightRadius: first ? 6 : undefined,
                  borderBottomLeftRadius: last ? 6 : undefined,
                  borderBottomRightRadius: last ? 6 : undefined,
                }}
                onMouseEnter={() => setActive(layer.name)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(layer.name)}
                onBlur={() => setActive(null)}
                tabIndex={0}
              >
                <div className="text-[11px] sm:text-sm font-semibold text-slate-100">{layer.name}</div>
                <div className="text-[9px] sm:text-[11px] text-slate-400 leading-snug mt-1">
                  {layer.ours}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* footers, kept in a matching two-column row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-6 mt-2">
        <p className="text-[10px] sm:text-[11px] text-center text-slate-600">
          two vendor seams
        </p>
        <p className="text-[10px] sm:text-[11px] text-center text-slate-500">
          no vendor seam — nothing to renegotiate
        </p>
      </div>

      {/* what the hovered layer earns us, or the summary */}
      <div className="mt-5 pt-4 border-t border-slate-700/40 min-h-[3.5rem]">
        {active ? (
          <p className="text-[13px] text-slate-300 leading-relaxed">
            <span className="text-slate-100 font-medium">{active}:</span>{' '}
            {layers.find((l) => l.name === active)?.gain}
          </p>
        ) : (
          <p className="text-[13px] text-slate-400 leading-relaxed">
            <span className="text-white font-semibold">
              Every seam is a margin leak and a place accountability breaks.
            </span>{' '}
            Running on our own stack, Fnk, is why DialogMe can price per conversation rather than
            per token, offer a private deployment without shipping someone else’s licence, and answer for the whole system when something goes wrong. Hover a layer
            for what it earns.
          </p>
        )}
      </div>
    </figure>
  );
}
